import Image from 'components/Basic/Image/Image';
import { GoogleMap } from 'components/Basic/Map/GoogleMap/GoogleMap';
import { PigeonMap } from 'components/Basic/Map/PigeonMap/PigeonMap';
import { SeznamMap } from 'components/Basic/Map/SeznamMap/SeznamMap';
import SelectStoreButton from 'components/Blocks/Store/SelectStoreButton/SelectStoreButton';
import StoreBenefits from 'components/Blocks/Store/StoreBenefits';
import UserText from 'components/Helpers/UserText/UserText';
import { Webline } from 'components/Layout/Webline/Webline';
import OpeningHoursTable from 'components/Pages/StoreDetail/OpeningHoursTable';
import {
    DescriptionStyled,
    ImagesWrapperStyled,
    InfoItemOpeningHoursStyled,
    InfoItemStyled,
    MapStyled,
    StoreBenefitsWrapperStyled,
    StoreDetailContentStyled,
    StoreDetailStyled,
    TelephoneStyled,
} from 'components/Pages/StoreDetail/StoreDetail.style';
import { MapProvider } from 'context/Map/MapProvider';
import { createNumberAriaLabel } from 'helpers/accessibility/ariaLabelHelpers';
import useTranslation from 'next-translate/useTranslation';
import dynamic from 'next/dynamic';
import { useMemo, useState } from 'react';
import { MapMarkerType, MapProviderTypeEnum } from 'types/map';
import { StoreDetailType } from 'types/store';

const Gallery = dynamic(() => import('components/Basic/Gallery/Gallery').then((component) => component.Gallery));

type StoreDetailPageProps = {
    store: StoreDetailType;
    fetching: boolean;
};

const TEST_IDENTIFIER = 'pages-storedetail';

export const StoreDetail: FC<StoreDetailPageProps> = ({ store, fetching }) => {
    const { t } = useTranslation();
    const [activeGalleryIndex, setActiveGalleryIndex] = useState<number | undefined>();

    const markers = useMemo(
        () => [
            {
                identifier: store.uuid,
                lat: store.locationLatitude,
                lng: store.locationLongitude,
                storeName: store.storeName,
                storeStreet: store.street,
                storeCity: store.city,
                storeSlug: store.slug,
                isPickupPoint: store.isPickupPoint,
            } as MapMarkerType,
        ],
        [store],
    );

    const mapSetting = useMemo(
        () => ({
            latitude: store.locationLatitude!,
            longitude: store.locationLongitude!,
            zoom: 15,
        }),
        [store],
    );

    const hasSomeVisibleBenefits: boolean =
        (store.acceptGiftCoupon && !!store.acceptGiftCouponIcon) ||
        (store.hasLoyaltyProgram && !!store.hasLoyaltyProgramIcon) ||
        (store.isPickupPoint && !!store.isPickupPointIcon);

    return (
        <Webline data-testid={TEST_IDENTIFIER}>
            <StoreDetailStyled>
                <StoreDetailContentStyled>
                    <h1 data-testid="basic-heading-h1">{store.storeName}</h1>
                    <div>
                        {store.isPickupPoint && <SelectStoreButton storeUuid={store.uuid} />}
                        {store.description !== null && (
                            <DescriptionStyled>
                                <UserText htmlContent={store.description} />
                            </DescriptionStyled>
                        )}
                        {store.openingHours !== null &&
                            store.openingHours.days.some(({ alwaysClosed }) => !alwaysClosed) && (
                                <InfoItemOpeningHoursStyled>
                                    <span
                                        className={`mb-6 inline-block px-1 py-1.5 text-tiny leading-2 text-white ${
                                            store.openingHours.isCurrentlyOpen ? 'bg-greenContrast' : 'bg-red'
                                        }`}
                                    >
                                        {store.openingHours.isCurrentlyOpen ? t('Open') : t('Closed')}
                                    </span>
                                    <h6>{t('Opening hours')}</h6>
                                    <OpeningHoursTable {...store.openingHours} />
                                </InfoItemOpeningHoursStyled>
                            )}
                        <h6>{t('Address')}</h6>
                        <InfoItemStyled>
                            {`${store.street} ${store.city} ${store.postcode}`}
                            <br />
                            {t('Email address')}:{' '}
                            {store.email !== null && (
                                <a href={'mailto:' + store.email} tabIndex={0}>
                                    {store.email}
                                </a>
                            )}
                        </InfoItemStyled>

                        {store.telephone !== null && (
                            <TelephoneStyled>
                                {t('Phone')}:{' '}
                                <a
                                    aria-label={createNumberAriaLabel(store.telephone)}
                                    href={'tel:' + store.telephone}
                                    tabIndex={0}
                                >
                                    {store.telephone}
                                </a>
                            </TelephoneStyled>
                        )}

                        {store.locationLatitude !== null && store.locationLongitude !== null && (
                            <InfoItemStyled>
                                {`${t('GPS')}: ${store.locationLatitude
                                    .toString()
                                    .replace('.', ',')}N ${store.locationLongitude.toString().replace('.', ',')}E`}
                            </InfoItemStyled>
                        )}
                        {store.isOperatedByTescoma && (
                            <div>{t('Sales center operated by the company TESCOMA s.r.o. (IČ: 46981691)')}</div>
                        )}
                        {hasSomeVisibleBenefits && (
                            <StoreBenefitsWrapperStyled>
                                <h6>{t('In-store services')}:</h6>
                                <StoreBenefits
                                    acceptGiftCoupon={store.acceptGiftCoupon}
                                    acceptGiftCouponIcon={store.acceptGiftCouponIcon}
                                    hasLoyaltyProgram={store.hasLoyaltyProgram}
                                    hasLoyaltyProgramIcon={store.hasLoyaltyProgramIcon}
                                    isInline={false}
                                    isPickupPoint={store.isPickupPoint}
                                    isPickupPointIcon={store.isPickupPointIcon}
                                />
                            </StoreBenefitsWrapperStyled>
                        )}
                    </div>
                    {store.locationLatitude !== null && store.locationLongitude !== null && (
                        <MapStyled>
                            <MapProvider initSetting={mapSetting}>
                                {(provider: string | undefined) => {
                                    if (provider === MapProviderTypeEnum.OSM) {
                                        return <PigeonMap markers={markers} shouldShowSpinner={fetching} />;
                                    }
                                    if (provider === MapProviderTypeEnum.SEZNAM) {
                                        return <SeznamMap markers={markers} shouldShowSpinner={fetching} />;
                                    }
                                    if (provider === MapProviderTypeEnum.GOOGLE) {
                                        return <GoogleMap markers={markers} shouldShowSpinner={fetching} />;
                                    }

                                    return null;
                                }}
                            </MapProvider>
                        </MapStyled>
                    )}
                </StoreDetailContentStyled>
                {store.images.length > 0 && (
                    <ImagesWrapperStyled>
                        <div className="flex flex-wrap items-center justify-center lg:justify-start">
                            {store.images.map((image, index) => (
                                <button
                                    key={image}
                                    className="m-2.5 xl:m-5"
                                    tabIndex={0}
                                    onClick={() => setActiveGalleryIndex(index)}
                                >
                                    <Image
                                        alt={store.storeName}
                                        className="h-[167px] w-[250px]"
                                        height="167"
                                        src={image}
                                        width="250"
                                    />
                                </button>
                            ))}
                        </div>
                        {activeGalleryIndex !== undefined && (
                            <Gallery
                                galleryItems={store.images.map((image) => ({ src: image }))}
                                startIndex={activeGalleryIndex}
                                onCloseCallback={() => setActiveGalleryIndex(undefined)}
                            />
                        )}
                    </ImagesWrapperStyled>
                )}
            </StoreDetailStyled>
        </Webline>
    );
};

export default StoreDetail;
